import { IonIcon } from "@ionic/react";
import { banOutline, calendarOutline, informationCircleOutline, personOutline, trainOutline } from "ionicons/icons";
import React from "react";
import { useReducedMotion } from "../../hooks/useReducedMotion.hook";
import { CitySupplementBadge } from "../shared/CitySupplementBadge";
import { TicketBarcode } from "./TicketBarcode";
import { TicketCardBack } from "./TicketCardBack";
import { TicketProviderBadgeMark } from "./TicketProviderBadgeMark";
import { CARD_SHADOW_FILTER, FOOTER_FLEX_GROW, notchMask, STUB_FLEX_GROW } from "./ticketCardSkin";
import { formatQualityOfService, formatTicketValidityBounds, type TicketViewerItem } from "./ticketViewer.tools";

/**
 * Props of {@link TicketCard}.
 */
export interface TicketCardProps {
    "item": TicketViewerItem;

    /** Backend provider id (`purchase.provider`) driving the badge. */
    "provider": string;

    /** Present on background cards: activates (slides to) the card. */
    "onActivate"?: () => void;

    /** Whether the card shows its back face. */
    "flipped"?: boolean;

    /** Flips the card to its back face — shown only when a description exists. */
    "onFlip"?: () => void;

    /** Flips the card back to its front face. */
    "onFlipBack"?: () => void;

    /** Opens the refund panel for this ticket (triggered from the back face). */
    "onRequestRefund"?: () => void;
}

/**
 * Long weekday/date formatting (`Vendredi 27 mars`) for the ticket footer.
 */
const formatTicketDate = ( iso: string ): string => {
    const date = new Date( iso );

    if ( Number.isNaN( date.getTime() ) ) {
        return "";
    }

    const formatted = new Intl.DateTimeFormat( "fr-CH", {
        "weekday": "long",
        "day":     "numeric",
        "month":   "long",
    } ).format( date ).
        replace( /,\s*/u, " " );

    return formatted.charAt( 0 ).toUpperCase() + formatted.slice( 1 );
};

/** Short HH:mm formatting for leg endpoints. */
const formatTicketTime = ( iso: string ): string => {
    const date = new Date( iso );

    return Number.isNaN( date.getTime() )
        ? ""
        : new Intl.DateTimeFormat( "fr-CH", {
            "hour":   "2-digit",
            "minute": "2-digit",
        } ).format( date );
};

/** Hides a flip face from hit-testing and readers while turned away. */
const BACKFACE_HIDDEN: React.CSSProperties = {
    "backfaceVisibility":       "hidden",
    "WebkitBackfaceVisibility": "hidden",
};

/**
 * One ticket card: provider logo, large barcode zone, perforation with real
 * punched notches (mask-cut on both edges of every card), and the
 * issued-ticket metadata footer (product, route, travel date, passenger,
 * validity). City-Ticket offers are marked as `Billet de parcours` with their
 * supplemented areas rendered as pills.
 *
 * @remarks The card stays light ("paper") in both color schemes on purpose —
 * maximum barcode contrast for scanners, matching wallet conventions. The
 * paper background lives on the masked stub/footer sections so the notches
 * are actual holes through the card, not deck-coloured discs.
 * @param props - Component props.
 * @returns The ticket visual.
 */
export const TicketCard: React.FC<TicketCardProps> = ( {
    item,
    provider,
    onActivate,
    flipped = false,
    onFlip,
    onFlipBack,
    onRequestRefund,
} ) => {
    const flipDurationMs = useReducedMotion() ? 150 : 550;
    const details = item.details ?? null;
    const refundStatus = item.ticket.refundStatus ?? "none";
    const isRefunded = refundStatus === "refunded" || refundStatus === "refund_pending";

    const travelDate = details?.travelDate ?? item.fromTime;
    const recapDate = travelDate === undefined ? "" : formatTicketDate( travelDate );
    const recapFromTime = item.fromTime === undefined ? "" : formatTicketTime( item.fromTime );
    const recapToTime = item.toTime === undefined ? "" : formatTicketTime( item.toTime );
    const recapTimesLine = recapFromTime !== "" && recapToTime !== "" ? `${ recapFromTime } → ${ recapToTime }` : recapFromTime || recapToTime;
    const validityBounds = formatTicketValidityBounds( details );
    const singleBound = validityBounds.from ?? validityBounds.to;
    const validityRows: {
        "label": string;
        "value": string;
    }[] = validityBounds.from !== null && validityBounds.to !== null && validityBounds.from !== validityBounds.to
        ? [
            {
                "label": "du",
                "value": validityBounds.from,
            },
            {
                "label": "au",
                "value": validityBounds.to,
            },
        ]
        : singleBound === null
            ? []
            : [
                {
                    "label": "le",
                    "value": singleBound,
                },
            ];
    const serviceClass = formatQualityOfService( details?.qualityOfService );
    const description = details?.description ?? "";
    const citySupplements = details?.citySupplements ?? [];
    const isJourneyTicket = citySupplements.length > 0;
    const productTitle = isJourneyTicket ? "Billet de parcours" : details?.productDescription ?? ( item.isUnmatched ? "Billet" : "Billet CFF" );

    const handleActivateKeyDown = ( event: React.KeyboardEvent ): void => {
        if ( onActivate !== undefined && ( event.key === "Enter" || event.key === " " ) ) {
            event.preventDefault();
            onActivate();
        }
    };

    const cardBody = (
        <>
            {/*
             * Stub (logo + barcode): the outer wrapper masks the bottom-left
             * bite, the inner one the bottom-right — two real punched
             * quarter-notches on the perforation edge.
             */}
            <div
                className="flex min-h-0 flex-col"
                data-testid="ticket-card-stub"
                style={{
 "flex": STUB_FLEX_GROW,
...notchMask( "0%", "100%" ),
}}
            >
                <div
                    className="flex min-h-0 flex-1 flex-col bg-[#fdfdfb]"
                    style={notchMask( "100%", "100%" )}
                >
                    {/* Header: provider logo (or pill fallback) + ticket reference. */}
                    <div className="flex items-center justify-between gap-2 px-4 pt-4">
                        <TicketProviderBadgeMark provider={provider} />
                        <span className="flex min-w-0 flex-col items-end text-right leading-tight">
                            <span className="text-[0.55rem] font-semibold uppercase tracking-widest text-slate-400">
                                Billet
                            </span>
                            <span className="max-w-full truncate text-[0.65rem] font-medium tracking-wide text-slate-500">
                                {item.ticket.externalTicketReference}
                            </span>
                        </span>
                    </div>
                    {/* Barcode zone: elastic, always white for scanners; replaced by a clear invalidated stamp once refunded. */}
                    <div className="flex min-h-[150px] flex-1 items-center justify-center px-5 py-3">
                        <div className="relative aspect-square h-full max-h-full max-w-full overflow-hidden rounded-xl bg-white p-2">
                            {isRefunded
                                ? (
                                    <div
                                        className="flex h-full w-full flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-amber-300 bg-amber-50/90 p-3 text-center"
                                        data-testid="ticket-refunded-badge"
                                    >
                                        <IonIcon
                                            aria-hidden={true}
                                            className="h-7 w-7 text-amber-600"
                                            icon={banOutline}
                                        />
                                        <span className="text-xs font-extrabold uppercase tracking-wider text-amber-800">
                                            {refundStatus === "refund_pending" ? "Remboursement en cours" : "Billet remboursé"}
                                        </span>
                                        <span className="text-[10px] font-medium leading-tight text-amber-700">
                                            Ce titre de transport a été annulé et n&apos;est plus valable.
                                        </span>
                                    </div>
                                )
                                : <TicketBarcode ticket={item.ticket} />}
                        </div>
                    </div>
                </div>
            </div>
            {/*
             * Footer (perforation + metadata): same bite trick at the top
             * corners — the two halves compose a punched notch on each edge.
             */}
            <div
                className="flex min-h-0 flex-col"
                data-testid="ticket-card-footer"
                style={{
 "flex": FOOTER_FLEX_GROW,
...notchMask( "0%", "0%" ),
}}
            >
                <div
                    className="flex min-h-0 flex-1 flex-col bg-[#fdfdfb]"
                    style={notchMask( "100%", "0%" )}
                >
                    {/* Perforation flush with the stub/footer boundary — centred on the notches. */}
                    <div className="shrink-0 border-t-2 border-dashed border-slate-300" />
                    {/* Footer: issued-ticket metadata + this segment's recap — never the whole itinerary. */}
                    <div className="flex min-h-0 flex-1 flex-col justify-between gap-2 px-4 pb-3 pt-3">
                <div className="flex flex-col gap-1">
                    <div className="flex items-center justify-between gap-2">
                        <p className="min-w-0 truncate text-sm font-bold text-slate-900">
                            {productTitle}
                        </p>
                        <div className="flex shrink-0 items-center gap-1">
                            {isJourneyTicket && details?.productDescription !== undefined
                                ? (
                                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[0.65rem] font-bold text-slate-600">
                                        {details.productDescription}
                                    </span>
                                )
                                : null}
                            {serviceClass !== null
                                ? (
                                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[0.65rem] font-bold text-slate-600">
                                        {serviceClass}
                                    </span>
                                )
                                : null}
                        </div>
                    </div>
                    {item.isUnmatched
                        ? (
                            <p className="text-xs font-medium text-slate-500">
                                Segment non associé
                            </p>
                        )
                        : (
                            <>
                                <p className="min-w-0 truncate text-sm font-semibold text-slate-800">
                                    {item.fromName ?? "—"}
                                    <span
                                        aria-hidden={true}
                                        className="mx-1.5 font-normal text-slate-400"
                                    >
→
                                    </span>
                                    {item.toName ?? "—"}
                                </p>
                                <p className="flex items-center gap-1 text-xs font-medium text-slate-500">
                                    <IonIcon
                                        aria-hidden={true}
                                        className="h-3.5 w-3.5 shrink-0 text-slate-400"
                                        icon={trainOutline}
                                    />
                                    <span className="flex min-w-0 flex-col leading-tight">
                                        {recapDate !== ""
                                            ? (
                                                <span className="truncate">
                                                    {recapDate}
                                                </span>
                                            )
                                            : null}
                                        {recapTimesLine !== ""
                                            ? (
                                                <span className="truncate">
                                                    {recapTimesLine}
                                                </span>
                                            )
                                            : null}
                                    </span>
                                </p>
                            </>
                        )}
                    {citySupplements.length > 0
                        ? (
                            <div
                                className="mt-2 flex flex-col items-start gap-1.5"
                                data-testid="ticket-city-supplements"
                            >
                                {citySupplements.map( ( supplement ) => (
                                    <CitySupplementBadge
                                        areaName={supplement.areaName}
                                        city={supplement.label.replace( /^Suppl\.\s*/u, "" ).replace( /^City\s*/u, "" )}
                                        isDarkUi={false}
                                        key={`${ supplement.direction }:${ supplement.label }`}
                                    />
                                ) )}
                            </div>
                        )
                        : null}
                </div>
                <div className="flex flex-col gap-1">
                    {details?.passengerName !== undefined
                        ? (
                            <p className="flex items-center gap-1 text-xs font-medium text-slate-600">
                                <IonIcon
                                    aria-hidden={true}
                                    className="h-3.5 w-3.5 shrink-0 text-slate-400"
                                    icon={personOutline}
                                />
                                {details.passengerName}
                            </p>
                        )
                        : null}
                    <div className="flex items-end justify-between gap-2">
                        {validityRows.length > 0
                            ? (
                                <p className="flex items-center gap-1 text-[0.65rem] font-medium text-slate-400">
                                    <IonIcon
                                        aria-hidden={true}
                                        className="h-3.5 w-3.5 shrink-0"
                                        icon={calendarOutline}
                                    />
                                    {/*
                                     * Two-column grid: the label column sizes to
                                     * "Valable du" so the bound dates align
                                     * vertically on their own column.
                                     */}
                                    {/*
                                     * Three-column grid: "Valable" spans the
                                     * bound rows vertically centred, then the
                                     * du/au/le labels, then the dates on their
                                     * own aligned column.
                                     */}
                                    <span
                                        className="grid items-center gap-x-1"
                                        style={{ "gridTemplateColumns": "auto auto 1fr" }}
                                    >
                                        <span
                                            className="self-center"
                                            style={{ "gridRow": `span ${ validityRows.length }` }}
                                        >
                                            Valable
                                        </span>
                                        {validityRows.map( ( row ) => (
                                            <span
                                                className="contents"
                                                key={row.label}
                                            >
                                                <span className="text-right">
{row.label}
                                                </span>
                                                <span>
{row.value}
                                                </span>
                                            </span>
                                        ) )}
                                    </span>
                                </p>
                            )
                            : <span />}
                        {( description !== "" || onRequestRefund !== undefined || isRefunded ) && onFlip !== undefined
                            ? (
                                <button
                                    aria-label="Informations du billet"
                                    className="-mb-1 -mr-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-slate-400 transition-colors duration-150 hover:bg-slate-100 hover:text-slate-600 focus-visible:outline-none focus-visible:ring-2"
                                    data-testid="ticket-info-button"
                                    onClick={( event ) => {
                                        event.stopPropagation();
                                        onFlip();
                                    }}
                                    tabIndex={flipped ? -1 : 0}
                                    type="button"
                                >
                                    <IonIcon
                                        aria-hidden={true}
                                        className="h-[18px] w-[18px]"
                                        icon={informationCircleOutline}
                                    />
                                </button>
                            )
                            : null}
                    </div>
                </div>
                    </div>
                </div>
            </div>
        </>
    );

    const cardClassName = `flex h-full w-full flex-col rounded-2xl text-left ${ onActivate !== undefined ? "cursor-pointer transition-transform duration-150 ease-out hover:scale-[1.01] focus-visible:outline-none focus-visible:ring-2" : "" }`;

    return (
        <div
            aria-label={onActivate !== undefined ? "Afficher ce billet" : undefined}
            className={cardClassName}
            data-testid="ticket-card"
            onClick={onActivate}
            onKeyDown={handleActivateKeyDown}
            role={onActivate !== undefined ? "button" : undefined}
            style={{
                "filter":      CARD_SHADOW_FILTER,
                "perspective": "1100px",
            }}
            tabIndex={onActivate !== undefined ? 0 : undefined}
        >
            {/*
             * 3D flip: the inner element owns its own `preserve-3d` context —
             * independent from the surrounding Swiper slide transforms — while
             * the root `perspective` sets the camera and the `filter` shadow
             * follows the card through the turn.
             */}
            <div
                className="relative h-full w-full"
                data-testid="ticket-card-flipper"
                style={{
                    "transform":      flipped ? "rotateY(180deg)" : "rotateY(0deg)",
                    "transformStyle": "preserve-3d",
                    "transition":     `transform ${ flipDurationMs }ms cubic-bezier(0.4, 0, 0.2, 1)`,
                }}
            >
                {/*
                 * `rounded-2xl overflow-hidden` lives on each face, not the
                 * root: mid-turn the perspective projects the approaching edge
                 * beyond the card's own box — a root clip would cut it.
                 */}
                <div
                    aria-hidden={flipped}
                    className="flex h-full flex-col overflow-hidden rounded-2xl"
                    data-testid="ticket-card-front"
                    style={BACKFACE_HIDDEN}
                >
                    {cardBody}
                </div>
                <div
                    aria-hidden={!flipped}
                    className="absolute inset-0 overflow-hidden rounded-2xl"
                    style={{
                        ...BACKFACE_HIDDEN,
                        "transform": "rotateY(180deg)",
                    }}
                >
                    <TicketCardBack
                        active={flipped}
                        item={item}
                        onFlipBack={onFlipBack}
                        onRequestRefund={onRequestRefund}
                        provider={provider}
                    />
                </div>
            </div>
        </div>
    );
};
